import { useState } from 'react';
import { Link } from 'react-router-dom';
import BrandIntro from '../components/BrandIntro';

const RANGES = ['Under $50K', '$50K – $250K', '$250K – $1M', '$1M+'];

export default function Landing() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [investmentRange, setInvestmentRange] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`/api/investor-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: fullName,
          email,
          investment_range: investmentRange,
          message
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="landing-page" data-testid="landing-page">
      <BrandIntro />
      <div className="global-watermark">
        <img src="/brand/nwg-logo-web.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." />
      </div>

      <header className="landing-header">
        <div className="landing-brand">
          <img src="/brand/nwg-logo-small.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." />
          <div>
            <div className="landing-brand-name">NextWealth™ Global</div>
            <div className="landing-brand-sub">NWG COMMAND CENTER</div>
          </div>
        </div>
        <Link to="/login" className="landing-login-btn" data-testid="landing-login-btn">
          ENTER NWG COMMAND CENTER
        </Link>
      </header>

      <main className="landing-main">
        <section className="landing-hero">
          <div className="landing-hero-tag">SOVEREIGN WEALTH INFRASTRUCTURE</div>
          <h1 className="landing-title">
            Command Your Capital.<br />
            <span>Secure Your Legacy.</span>
          </h1>
          <p className="landing-lead">
            A private command operating system unifying capital deployment,
            legacy preservation, and growth intelligence — engineered for
            families and investors who operate on their own terms.
          </p>
          <div className="landing-cta-row">
            <a href="#investor-interest" className="landing-cta-primary" data-testid="landing-cta-invest">
              REQUEST INVESTOR ACCESS
            </a>
            <Link to="/login" className="landing-cta-secondary" data-testid="landing-cta-login">
              MEMBER LOGIN →
            </Link>
          </div>
        </section>

        <section className="landing-pillars">
          <div className="landing-pillar" data-testid="pillar-command">
            <div className="landing-pillar-num">01</div>
            <h3>COMMAND</h3>
            <p>Mission control, secure vault storage, investor center and AI-driven capital intelligence — one operational core.</p>
          </div>
          <div className="landing-pillar" data-testid="pillar-legacy">
            <div className="landing-pillar-num">02</div>
            <h3>LEGACY</h3>
            <p>Family vault, education systems and generational planning designed to preserve what you build.</p>
          </div>
          <div className="landing-pillar" data-testid="pillar-growth">
            <div className="landing-pillar-num">03</div>
            <h3>GROWTH</h3>
            <p>Funnels, campaigns, analytics and affiliate systems that compound reach into revenue.</p>
          </div>
        </section>

        <section className="landing-interest" id="investor-interest">
          <div className="landing-interest-copy">
            <h2>INVESTOR INTEREST</h2>
            <p>
              Access to the NextWealth™ Global network is by review. Submit your interest and
              our team will reach out with next steps.
            </p>
          </div>

          {submitted ? (
            <div className="landing-form-success" data-testid="lead-success">
              <div className="landing-success-mark">✓</div>
              <h3>Interest Received</h3>
              <p>Thank you. Our team will be in touch shortly.</p>
            </div>
          ) : (
            <form className="landing-form" onSubmit={handleSubmit} data-testid="lead-form">
              <div className="landing-form-row">
                <input
                  type="text"
                  placeholder="Full name"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  required
                  data-testid="lead-name-input"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  data-testid="lead-email-input"
                />
              </div>
              <select
                value={investmentRange}
                onChange={e => setInvestmentRange(e.target.value)}
                data-testid="lead-range-select"
              >
                <option value="">Investment range (optional)</option>
                {RANGES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <textarea
                placeholder="Tell us about your goals (optional)"
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                data-testid="lead-message-input"
              />
              {error && <div className="landing-form-error" data-testid="lead-error">{error}</div>}
              <button type="submit" disabled={loading} data-testid="lead-submit-btn">
                {loading ? 'SUBMITTING...' : 'SUBMIT INTEREST'}
              </button>
            </form>
          )}
        </section>
      </main>

      <footer className="landing-footer">
        <span>© {new Date().getFullYear()} NextWealth™ Global</span>
        <span data-testid="powered-by-nwg">Powered by NextWealth™ Global</span>
        <span>Private • By review only • Not a public offering</span>
      </footer>
    </div>
  );
}
